import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LEVELS, LEVEL_INFO } from '../game/types.ts';
import { BackIcon, CloseIcon } from '../components/Icons.tsx';
import { HelpVideo } from './HelpVideo.tsx';
import { MiniBoard } from './MiniBoard.tsx';
import { EXAMPLES, TECHNIQUE_INFO, exampleView } from './techniques.ts';

type View = 'cards' | 'rules' | 'techniques';

/** A real position used for the rule pictures. */
const SAMPLE = EXAMPLES['hidden-single'].values;
const firstEmpty = SAMPLE.findIndex((v) => v === 0);
const row = (r: number) => Array.from({ length: 9 }, (_, i) => r * 9 + i);
/** The same position with a digit placed twice in a row, for the "repeated digits" picture. */
const CLASH = (() => {
  const v = SAMPLE.slice();
  const r = Math.floor(firstEmpty / 9);
  const given = row(r).find((c) => v[c] && c !== firstEmpty)!;
  v[firstEmpty] = v[given];
  return { values: v, cells: [firstEmpty, given] };
})();
/** Notes on the first row's empty cells, with one struck off, for the notes picture. */
const NOTES_DEMO = (() => {
  const notes = new Array(81).fill(0);
  const struck = new Array(81).fill(0);
  const cand = EXAMPLES['hidden-single'].candidates;
  const r = Math.floor(firstEmpty / 9);
  for (const c of row(r)) if (!SAMPLE[c]) notes[c] = cand[c];
  const d = [1, 2, 3, 4, 5, 6, 7, 8, 9].find((x) => notes[firstEmpty] & (1 << x));
  if (d) struck[firstEmpty] = 1 << d;
  return { notes, struck, rows: [Math.floor(r / 3) * 3, Math.floor(r / 3) * 3 + 2] as [number, number] };
})();

const isDesktop = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

const SHORTCUTS: [string, string][] = [
  ['1–9', 'Enter a digit (or a note, when Notes is on)'],
  ['Backspace, Delete or 0', 'Erase the selected cell'],
  ['Arrow keys', 'Move between cells'],
  ['N', 'Notes on or off'],
  ['A', 'Fill all possibilities'],
  ['P', 'Pause or resume'],
  ['Esc', 'Close a hint'],
  ['Ctrl/⌘ Z', 'Undo'],
  ['Ctrl Y or ⌘ Shift Z', 'Redo'],
];

function TechniqueFigure({ id, big }: { id: string; big?: boolean }) {
  const ex = EXAMPLES[id];
  const info = TECHNIQUE_INFO.find((t) => t.id === id)!;
  const view = exampleView(ex);
  return (
    <figure className={`tech-figure${big ? ' is-big' : ''}`}>
      <MiniBoard
        values={ex.values}
        notes={ex.candidates}
        pattern={ex.step.cells}
        area={view.area}
        placements={ex.step.placements}
        eliminations={ex.step.eliminations}
        rows={view.rows}
        cols={view.cols}
        label={`${info.name} example: ${ex.step.message}`}
      />
      <figcaption>{ex.step.message}</figcaption>
    </figure>
  );
}

const Key = () => (
  <p className="fig-key">
    <span className="key-swatch pattern" /> pattern <span className="key-swatch target" /> digit that goes in{' '}
    <span className="key-swatch elim">4</span> possibility removed
  </p>
);

// ---------------------------------------------------------------- cards

interface Card {
  title: string;
  body: ReactNode;
  art?: ReactNode;
  desktopOnly?: boolean;
}

function cards(go: (v: View) => void): Card[] {
  return [
    {
      title: 'The rules',
      body: <p>Fill every empty cell so each row, each column and each 3×3 box contains 1 to 9 exactly once.</p>,
      art: <HelpVideo name="how-to-play" label="Video: how to play" />,
    },
    {
      title: 'Five levels',
      body: (
        <p>
          Each level is set by the hardest technique a puzzle needs, from Easy (singles only) to Experts Only (chains).
          Every puzzle has one solution and never needs guessing.
        </p>
      ),
      art: (
        <ol className="card-levels">
          {LEVELS.map((l, i) => (
            <li key={l}>
              <span className="card-bars" aria-hidden="true">
                {LEVELS.map((_, p) => (
                  <i key={p} className={p <= i ? 'on' : ''} />
                ))}
              </span>
              {LEVEL_INFO[l].name}
            </li>
          ))}
        </ol>
      ),
    },
    {
      title: 'Entering numbers',
      body: <p>Tap a cell, then a number. Tap the same number again, or Erase, to clear it. Bold numbers are given and can’t change.</p>,
      art: <MiniBoard values={SAMPLE} selected={firstEmpty} rows={[0, 2]} label="A selected cell with its row, column and box shaded" />,
    },
    {
      title: 'Notes',
      body: (
        <p>
          The first time you tap Notes, choose <b>Fill all</b> (every possible digit, kept up to date) or <b>Start blank</b>.
          With Notes on, tap a number to strike it off; tap again to bring it back.
        </p>
      ),
      art: (
        <MiniBoard
          values={SAMPLE}
          notes={NOTES_DEMO.notes}
          struck={NOTES_DEMO.struck}
          rows={NOTES_DEMO.rows}
          label="Notes in a row, with one digit struck off faintly"
        />
      ),
    },
    {
      title: 'Mistakes',
      body: (
        <p>
          A digit repeated in a row, column or box turns red straight away. <b>Check</b> marks wrong cells only when you ask,
          and each use is counted.
        </p>
      ),
      art: <MiniBoard values={CLASH.values} clash={CLASH.cells} rows={[0, 2]} label="A digit repeated in a row, shown in red" />,
    },
    {
      title: 'Hints',
      body: (
        <p>
          <b>Hint</b> says where to look. <b>Show me</b> explains the step. <b>Another hint</b> shows a different one. Each new
          hint counts once; seeing one again is free.
        </p>
      ),
      art: <TechniqueFigure id="hidden-single" />,
    },
    {
      title: 'Timer, pause and saving',
      body: (
        <p>
          The timer runs while the board is on screen and pauses by itself when you leave. Pause hides the board. Each level
          keeps one game in progress.
        </p>
      ),
    },
    {
      title: 'Stats',
      body: (
        <p>
          Completed games per level: how many, best and average time, split by whether Fill all was used, with average hints
          and checks.
        </p>
      ),
    },
    {
      title: 'Keyboard',
      desktopOnly: true,
      body: (
        <table className="shortcuts">
          <tbody>
            {SHORTCUTS.map(([k, what]) => (
              <tr key={k}>
                <th scope="row">
                  <kbd>{k}</kbd>
                </th>
                <td>{what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
    },
    {
      title: 'Techniques',
      body: (
        <>
          <p>Each level adds new techniques. This video walks through them; the techniques guide explains each with a picture.</p>
          <p className="card-links">
            <button type="button" className="link-btn" onClick={() => go('techniques')}>
              Open the techniques guide
            </button>
            <button type="button" className="link-btn" onClick={() => go('rules')}>
              Read the full rules
            </button>
          </p>
        </>
      ),
      art: <HelpVideo name="techniques" label="Video: solving techniques" />,
    },
  ];
}

function Cards({ go }: { go: (v: View) => void }) {
  const desktop = isDesktop();
  const list = cards(go).filter((c) => desktop || !c.desktopOnly);
  const [index, setIndex] = useState(0);
  const track = useRef<HTMLDivElement>(null);

  const show = (i: number) => {
    const el = track.current?.children[i] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  // Keep the dots in step with swiping.
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const onScroll = () => setIndex(Math.round(el.scrollLeft / el.clientWidth));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="cards">
      <div className="cards-track" ref={track} tabIndex={0} aria-label="How to play cards">
        {list.map((c, i) => (
          <section key={c.title} className="help-card" aria-label={`${i + 1} of ${list.length}: ${c.title}`}>
            <h2>{c.title}</h2>
            {c.art ? <div className="card-art">{c.art}</div> : null}
            <div className="card-body">{c.body}</div>
          </section>
        ))}
      </div>
      <div className="cards-nav">
        <button type="button" className="btn small" onClick={() => show(Math.max(0, index - 1))} disabled={index === 0}>
          Back
        </button>
        <span className="dots" aria-label={`Card ${index + 1} of ${list.length}`}>
          {list.map((c, i) => (
            <i key={c.title} className={i === index ? 'on' : ''} />
          ))}
        </span>
        <button
          type="button"
          className="btn small primary"
          onClick={() => show(Math.min(list.length - 1, index + 1))}
          disabled={index === list.length - 1}
        >
          Next
        </button>
      </div>
      <p className="cards-foot">
        <button type="button" className="link-btn" onClick={() => go('rules')}>
          Full rules
        </button>
        <button type="button" className="link-btn" onClick={() => go('techniques')}>
          Techniques guide
        </button>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- full rules

function Rules({ go }: { go: (v: View) => void }) {
  return (
    <article className="doc">
      <h2>The goal</h2>
      <p>
        A Sudoku board is a 9×9 grid divided into nine 3×3 boxes. Some cells start filled in; these are the given numbers,
        shown in bold, and they cannot be changed. Fill every empty cell with a digit from 1 to 9 so that each row, each
        column and each box contains every digit exactly once.
      </p>
      <MiniBoard values={SAMPLE} area={row(Math.floor(firstEmpty / 9))} label="A puzzle with one row shaded" />
      <p>
        Every puzzle here has exactly one solution, and it can always be reached by logic. You never need to guess.
      </p>

      <h2>Choosing a level</h2>
      <p>
        There are five levels. A puzzle’s level is set by the hardest technique a person needs to solve it, not by how many
        numbers are given.
      </p>
      <ul>
        {LEVELS.map((l) => (
          <li key={l}>
            <b>{LEVEL_INFO[l].name}:</b> {LEVEL_INFO[l].blurb}
          </li>
        ))}
      </ul>
      <p>
        Each level keeps one game in progress. On the home screen, <b>Resume</b> continues it; <b>New game</b> starts another
        and asks before replacing the saved one.
      </p>

      <h2>Entering numbers</h2>
      <p>
        Tap a cell to select it. Its row, column and box are shaded, and every cell with the same digit is highlighted. Tap a
        number on the pad to place it. Tapping the same number again clears the cell, as does <b>Erase</b>. Under each number,
        the pad shows how many of that digit are still to be placed.
      </p>
      <MiniBoard values={SAMPLE} selected={firstEmpty} rows={[0, 5]} label="A selected cell with its row, column and box shaded" />

      <h2>Notes</h2>
      <p>
        Notes are small digits that record what a cell could still be. The first time you tap <b>Notes</b> in a game, you
        choose how to start:
      </p>
      <ul>
        <li>
          <b>Fill all</b> puts in every digit each cell can take, judged only by its row, column and box. These notes stay up
          to date by themselves: place a digit and it disappears from the cells around it; erase it and it comes back. With
          Notes on, tap a number to strike it off a cell. It stays faintly visible, struck through, and tapping it again
          brings it back.
        </li>
        <li>
          <b>Start blank</b> gives you an empty board to write your own notes. With Notes on, tapping a number adds or removes
          it. When you place a digit, it is cleared from your notes in the same row, column and box. A note for a digit that
          is already placed nearby shows in red.
        </li>
      </ul>
      <MiniBoard
        values={SAMPLE}
        notes={NOTES_DEMO.notes}
        struck={NOTES_DEMO.struck}
        rows={NOTES_DEMO.rows}
        label="Filled notes with one digit struck off"
      />
      <p>
        While Notes is on, <b>Fill all possibilities</b> and <b>Clear all notes</b> appear above the buttons. Both ask before
        changing anything, and Undo reverses them. You can choose in Settings whether Notes asks, always fills, or always
        starts blank.
      </p>
      <p>Using Fill all at any point marks the game as solved with auto notes; stats keep those games separate.</p>

      <h2>Mistakes</h2>
      <p>
        If the same digit appears twice in a row, column or box, both cells turn red straight away, and so does that digit on
        the number pad. This is always on, because it only shows a broken rule, not the answer.
      </p>
      <MiniBoard values={CLASH.values} clash={CLASH.cells} rows={[0, 2]} label="A repeated digit shown in red" />
      <p>
        A digit can break no rule and still be wrong. Those are only shown when you press <b>Check</b>, which marks every
        wrong cell until you change it. Each use of Check is counted.
      </p>

      <h2>Hints</h2>
      <p>
        <b>Hint</b> finds the easiest logical next step and first tells you only where to look and which technique to use.
        <b> Show me</b> then explains the step and highlights the cells involved. <b>Another hint</b> shows a different step,
        even if you haven’t used the current one. Close the hint with <b>Got it</b> or the ✕.
      </p>
      <TechniqueFigure id="hidden-single" big />
      <p>
        Each hint you haven’t seen before in this game counts once. Seeing it again, and Show me, are free. When there are no
        new hints left, Another hint goes round the earlier ones and says they aren’t counted. If a digit is repeated on the
        board, Hint asks you to fix that first; if there is a wrong digit, it suggests Check.
      </p>

      <h2>Timer, pause and saving</h2>
      <p>
        The timer starts when the board appears. It pauses by itself whenever the game is out of view: another tab, another
        app, or a locked screen. <b>Pause</b> stops it and hides the board. You can hide the timer in Settings; the time is
        still recorded. Your game is saved after every move.
      </p>

      <h2>Stats</h2>
      <p>
        Stats cover completed games on this device. For each level they show how many you solved, your best time and your
        average time, separately for games with and without auto notes, plus the average hints and checks per game. Games
        started but not finished are noted but not counted. Reset stats asks twice, because it cannot be undone.
      </p>

      <h2>Keyboard shortcuts</h2>
      <table className="shortcuts">
        <tbody>
          {SHORTCUTS.map(([k, what]) => (
            <tr key={k}>
              <th scope="row">
                <kbd>{k}</kbd>
              </th>
              <td>{what}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Settings</h2>
      <p>
        Choose a Minimal or Playful style, a light, dark or system colour mode, whether Notes asks how to start, and whether
        the timer is shown.
      </p>
      <p>
        <button type="button" className="link-btn" onClick={() => go('techniques')}>
          Next: the techniques guide
        </button>
      </p>
    </article>
  );
}

// ---------------------------------------------------------------- techniques

function Techniques() {
  return (
    <article className="doc">
      <p className="doc-lead">
        Every technique below, with a real position where it applies. Small digits are a cell’s possibilities. Each level
        adds techniques to those before it.
      </p>
      <Key />
      {LEVELS.map((level) => (
        <section key={level} className="doc-level">
          <h2>{LEVEL_INFO[level].name}</h2>
          {TECHNIQUE_INFO.filter((t) => t.level === level).map((t) => (
            <div key={t.id} className="doc-tech">
              <h3>{t.name}</h3>
              <p>{t.long}</p>
              <TechniqueFigure id={t.id} big />
            </div>
          ))}
        </section>
      ))}
    </article>
  );
}

// ---------------------------------------------------------------- overlay

const TITLES: Record<View, string> = { cards: 'How to play', rules: 'Full rules', techniques: 'Techniques guide' };

/** How to play: cards, with the full rules and techniques guide one tap away. */
export function HowToPlay({ onClose }: { onClose: () => void }) {
  const [view, setView] = useState<View>('cards');
  const body = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const go = (v: View) => {
    setView(v);
    body.current?.scrollTo({ top: 0 });
  };

  return (
    <div className="help" role="dialog" aria-modal="true" aria-labelledby="help-title">
      <div className="help-inner" ref={body}>
        <header className="help-head">
          {view !== 'cards' ? (
            <button type="button" className="icon-btn" onClick={() => go('cards')} aria-label="Back to the cards">
              <BackIcon />
            </button>
          ) : null}
          <h1 id="help-title">{TITLES[view]}</h1>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close How to play" autoFocus>
            <CloseIcon />
          </button>
        </header>
        {view === 'cards' ? <Cards go={go} /> : view === 'rules' ? <Rules go={go} /> : <Techniques />}
      </div>
    </div>
  );
}
